import { configureApi, http, HttpError } from 'lp-requests'
import { middleware as configureMiddleware } from 'lp-redux-api'
import { flow, mapValues } from 'lodash'
import { getSessionToken } from './auth'
import { formatUrl, formatDate, downloadFile } from 'utils'
import { COMPANY_NAME } from 'config/portal'
import { ProductAgreementType } from '../config/products'

// Configure lp-redux-api middleware

// This function will be passed the request options before every request.
// You can use it to set additional options or override existing ones.
function before() {
  return {
    bearerToken: getSessionToken(),
  }
}
//recursively relace value with null in response data
function replaceValueWithNull(res, valueToReplace = 'Null') {
  if (res instanceof Array) {
    // res is an array of objects
    return res.map((obj) => replaceValueWithNull(obj, valueToReplace))
  } else if (res instanceof Object) {
    // res is an object
    return mapValues(res, (value) =>
      replaceValueWithNull(value, valueToReplace)
    )
  } else if (res === valueToReplace) {
    return null
  } else {
    return res
  }
}

async function simpleFetch(url, { accept }) {
  const formattedUrl = formatUrl(`${process.env.REACT_APP_API_URL}/${url}`)
  return fetch(formattedUrl, {
    method: 'GET',
    headers: {
      Accept: accept,
      Authorization: `Bearer ${getSessionToken()}`,
    },
  }).then((res) => {
    if (!res.ok) throw new HttpError(res.status, res.statusText, res)
    return res
  })
}
//fetch data and download file
async function fetchAndDownload(url, fetchOptions, downloadOptions) {
  return simpleFetch(url, fetchOptions)
    .then((res) => res.arrayBuffer())
    .then((buffer) => downloadFile(buffer, downloadOptions))
}
//fetch data with content type and download file
async function contentTypeFetchAndDownload(url, fetchOptions, downloadOptions) {
  return simpleFetch(url, fetchOptions).then(async (res) => {
    const contentType = res.headers.get('Content-Type')
    const contentTypeClean = contentType.split(';')[0]

    downloadOptions.mimeType = contentType

    if (contentTypeClean === 'application/csv') {
      downloadOptions.mimeType = 'text/csv'
      downloadFile(await res.arrayBuffer(), downloadOptions)
    } else if (
      contentTypeClean === 'application/pdf' ||
      contentTypeClean === 'application/zip'
    ) {
      const blob = new Blob([new Uint8Array(await res.arrayBuffer())])
      downloadFile(blob, downloadOptions)
    }
  })
}
//the response data will be processed by replaceValueWithNull before being further processed or returned
//by using flow to compose replaceValueWithNull into the onSuccess function, we ensure that this transformation is applied consistently to all successful API responses.
const onSuccess = flow([replaceValueWithNull])

// Any transformations of failed responses can go here.
function onFailure(res) {
  return res
}
//API configuration object
export const config = {
  root: process.env.REACT_APP_API_URL,
  before,
  onSuccess,
  onFailure,
  mode: 'cors',
  // Override lp-redux-api default
  // MS API expects requests & responses as camelCase pattern
  // Additionally, abbreviations & acronyms are received or returned as ALL CAPS ie. "TPACompany"
  // APIs with exceptions to this global default should be handled individually where they are defined
  decamelizeBody: false,
  decamelizeQuery: false,
  camelizeResponse: false,
}

export const middleware = configureMiddleware(http, config)
export const api = configureApi(config)

// APIs that require access to the response meta -----------

export const downloadSearchOrderResults = async (id, number) => {
  const url = `/searchOrders/${id}/results`
  const fileName = `${formatDate(Date.now(), {
    formattingString: 'ddMMMY',
  })}_${number}_SEARCH-RESULTS`
  const fetchOptions = {
    accept: [
      'application/csv',
      'application/pdf',
      'application/zip',
      'binary/octet-stream',
    ],
  }
  const downloadOptions = { fileName }

  return contentTypeFetchAndDownload(url, fetchOptions, downloadOptions)
}
//download individual agreement by id
export const downloadServicesAgreementFile = async (
  id,
  planName,
  productType
) => {
  const isTaxableAccounts =
    productType === ProductAgreementType.TAXABLE_ACCOUNTS
  const queryParam = isTaxableAccounts
    ? '?taxableCustody=TaxableCustodyAgreement'
    : ''
  const url = `/planAgreementFile/${id}${queryParam}`
  const agreementName = isTaxableAccounts
    ? 'Taxable Accounts Services Agreement'
    : 'Automatic Rollover Services Agreement'
  const fileName = `${COMPANY_NAME.medium} ${agreementName} ${planName}`
  const fetchOptions = { accept: 'binary/octet-stream' }
  const downloadOptions = { fileName, mimeType: 'application/pdf' }

  return fetchAndDownload(url, fetchOptions, downloadOptions)
}

export const downloadStableValueFactSheet = async (id, agreementTypeName) => {
  const url = `/availablePlanAgreementTypes/${id}`
  const fileName = `${agreementTypeName} Fact Sheet`
  const fetchOptions = { accept: 'binary/octet-stream' }
  const downloadOptions = { fileName, mimeType: 'application/pdf' }

  return fetchAndDownload(url, fetchOptions, downloadOptions)
}

export const downloadSearchServiceAgreementFile = async (
  serviceAgreementId,
  serviceAgreementName
) => {
  const url = `/searchServicesAgreement/attachment/${serviceAgreementId}`
  const fileName = `${COMPANY_NAME.medium} Search Services Agreement ${serviceAgreementName}`
  const fetchOptions = { accept: 'binary/octet-stream' }
  const downloadOptions = { fileName, mimeType: 'application/pdf' }

  return fetchAndDownload(url, fetchOptions, downloadOptions)
}

export const viewNotificationLetterTemplate = async (status) => {
  const url = `/notificationLetterTemplate?planStatus=${encodeURIComponent(
    status
  )}`
  const fetchOptions = { accept: 'binary/octet-stream' }
  const res = await simpleFetch(url, fetchOptions)
  if (!res || !res.ok) {
    const err = new Error('Failed to fetch PDF')
    err.status = res?.status
    throw err
  }
  const buffer = await res.arrayBuffer()
  return new Blob([buffer], { type: 'application/pdf' })
}

export const viewNotificationsMaterials = async (name) => {
  const url = `/nsExistingRecordkeeperMaterials?recordKeeperName=${encodeURIComponent(
    name
  )}`
  const fetchOptions = { accept: 'binary/octet-stream' }
  const res = await simpleFetch(url, fetchOptions)
  if (!res || !res.ok) {
    const err = new Error('Failed to fetch PDF')
    err.status = res?.status
    throw err
  }
  const buffer = await res.arrayBuffer()
  return new Blob([buffer], { type: 'application/pdf' })
}
